// B-RELEASE-ON-FAILURE (2026-06-13) — single source of truth for "should the
// seller stay on / resume this takeover session, or be released to a fresh
// wizard so they can retry?".
//
// THE PROBLEM: the takeover-app persists the session in the browser
// (localStorage 'takeover.sessionId' / '.step' / '.maxStep', sessionStorage
// 'sessionToken' / 'takeover.lastCreateAt', and the idempotency key). On load,
// App.jsx's hydration effect auto-resumes whenever sessionId + token exist,
// clearing only on a 404. So a seller whose takeover FAILED / parked is resumed
// straight back onto the dead session on every refresh — they can't start over.
//
// THE RULE (decide on the BACKEND status, never on which spinner/screen shows):
//   PIN   (resume / keep on page) ONLY when the session is genuinely
//         active-processing OR fully complete OR on a live seller-input
//         challenge the seller is actively completing OR in an active,
//         account-mutating recovery (recovering / recovering_password — see
//         B-NO-STARTOVER-ON-RECOVERY 2026-06-21).
//   RELEASE (drop ALL local session keys, render a fresh wizard from step 1)
//         for EVERYTHING else — failure, operator review, a stalled
//         "Try again" park, or any unknown / future status (default-to-release).
//
// Both the load/hydration path AND the live /state path in App.jsx are driven
// off classifySessionDisposition() so they can never disagree.
//
// Kill switch: RELEASE_ON_FAILURE_ENABLED (utils/featureFlags.js). When OFF the
// classifier returns 'pin' for everything, so both call sites are byte-identical
// to the legacy always-resume behavior.

import { clearIdempotencyKey } from './idempotencyKey';
import { RELEASE_ON_FAILURE_ENABLED } from './featureFlags';

// ---------------------------------------------------------------------------
// Status sets — separate named constants so they are trivial to adjust at
// review. Values are the backend `status` column vocabulary
// (takeover-api/app/services/session_state.py SessionStatus) PLUS the derived
// statuses GET /state projects from the proven step-log flags.
// ---------------------------------------------------------------------------

// PIN: the seller stays on / resumes the session. Two groups:
//   - completed            → success; show their confirmation screen
//   - active-processing    → the automation is genuinely mid-flight
// (initializing…submitted_login are the expanded vocabulary; current code keeps
// the column at 'in_progress' during active processing, but they are pinned
// here for forward-compat so a future status emit can't be mis-released.)
export const PIN_STATUSES = new Set([
  'completed',
  'in_progress',
  'initializing',
  'creating_profile',
  'opening_browser',
  'connecting_cdp',
  'linkedin_loading',
  'entering_credentials',
  'submitted_login',
]);

// LIVE seller-input challenges — an active session the seller is actively
// completing. These are derived by GET /state from the open challenge; the real
// `status` column stays 'in_progress'. OPEN DECISION (a): default PIN (a live
// challenge is an active session). Flip PIN_LIVE_CHALLENGES to false to RELEASE
// these too (one-line toggle).
export const LIVE_CHALLENGE_STATUSES = new Set([
  'waiting_for_2fa',
  'waiting_for_code',
  'waiting_for_app_approval',
  'waiting_for_credentials_retry',
  'waiting_for_input',
]);

// OPEN DECISION (a): PIN live seller-input challenges. Default true (PIN).
export const PIN_LIVE_CHALLENGES = true;

// B-NO-STARTOVER-ON-RECOVERY (2026-06-21): ACTIVE, account-mutating background
// recovery — a seller-initiated LinkedIn password reset (recovering_password) or
// an in-task infra reconnect (recovering). These are NOT parked/dead: the BE is
// still driving the browser and the session usually heals back to in_progress.
// For recovering_password the reset may ALREADY have changed the LinkedIn password
// (takeover-api v3_tasks._attempt_linkedin_password_reset pre-stores the fresh
// Keeper password and submits it to LinkedIn irreversibly), so abandoning it
// strands the account half-reset. PIN them so BOTH the live "Start over" CTA AND
// the refresh/hydration release stay OFF these screens — a seller tap or refresh
// can no longer orphan an in-flight recovery (which burns the buyer email +
// DICloak profile and then dead-ends the restart on the duplicate-account /
// same-linkedin_email idempotency gates in POST /login). The non-destructive
// delayed "Try again" on `recovering` (gated on can_retry) is unaffected — it
// re-dispatches the blocked leg in place rather than nuking the session. When a
// recovery genuinely fails the BE flips to terminal_failed / password_reset_failed
// (release-class → Start over restored), and a real failure-signal on the
// envelope still releases via hasFailureSignal below.
export const ACTIVE_RECOVERY_STATUSES = new Set([
  'recovering',
  'recovering_password',
]);

// For documentation / tests: the statuses that explicitly RELEASE. Not consumed
// by classify() (which defaults everything-not-pinned to release) — kept as the
// reviewable, asserted list of what the operator asked to release. NOTE:
// recovering / recovering_password moved to ACTIVE_RECOVERY_STATUSES above
// (pinned) on 2026-06-21 — see B-NO-STARTOVER-ON-RECOVERY.
export const RELEASE_STATUSES = new Set([
  'operator_review',
  'failed',
  'terminal_failed',
  'cancelled',
  'abandoned',
  'waiting_for_seller_retry',
]);

/**
 * True when the snapshot/envelope carries a failure signal that should force a
 * RELEASE even under an otherwise-active status. Checks both camelCase
 * (snapshot) and snake_case (state envelope) field names, plus a failed latest
 * login / email-change step log. Deliberately does NOT treat
 * last_recoverable_failure_kind as a signal: it lingers on a session that has
 * already recovered back to in_progress, and releasing an active session on a
 * stale breadcrumb would regress live takeovers.
 */
export function hasFailureSignal(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return false;
  if (snapshot.terminalReason || snapshot.terminal_reason) return true;
  if (snapshot.failureReason || snapshot.failure_reason) return true;
  const logs = [
    snapshot.latestLoginStepLog,
    snapshot.latestStepLog,
    snapshot.latestEmailChangeStepLog,
  ];
  for (const log of logs) {
    if (log && typeof log === 'object' && log.status === 'failed') return true;
  }
  return false;
}

/**
 * Classify a session snapshot / state envelope into 'pin' | 'release'.
 *
 * @param {object|null} snapshot - the /state envelope (preferred — its `status`
 *   is the authoritative, derived status) or the hydration snapshot (raw column
 *   `status`). Only `.status` + the optional failure-signal fields are read.
 * @param {{releaseEnabled?: boolean}} [opts] - override the kill switch (tests).
 * @returns {'pin'|'release'}
 */
export function classifySessionDisposition(snapshot, opts = {}) {
  const releaseEnabled = opts.releaseEnabled !== undefined
    ? opts.releaseEnabled
    : RELEASE_ON_FAILURE_ENABLED;

  // Kill switch OFF → byte-identical to legacy: never release.
  if (!releaseEnabled) return 'pin';

  // Missing / malformed payload → default-to-release (unknown disposition).
  // Call sites must NOT invoke this with a not-yet-loaded envelope (guard for
  // null before calling on the live path).
  if (!snapshot || typeof snapshot !== 'object') return 'release';

  const status = typeof snapshot.status === 'string' ? snapshot.status : '';

  // Failure-signal override — release even under an otherwise-active status.
  if (hasFailureSignal(snapshot)) return 'release';

  if (PIN_STATUSES.has(status)) return 'pin';
  // B-NO-STARTOVER-ON-RECOVERY (2026-06-21): active recovery → PIN (no Start over,
  // no refresh-release). Checked AFTER the failure-signal override so a recovering
  // session carrying a genuine terminal/failure signal still releases.
  if (ACTIVE_RECOVERY_STATUSES.has(status)) return 'pin';
  if (LIVE_CHALLENGE_STATUSES.has(status)) {
    return PIN_LIVE_CHALLENGES ? 'pin' : 'release';
  }

  // operator_review, failed, terminal_failed, cancelled, abandoned,
  // waiting_for_seller_retry, and any unknown/future status → release.
  // (recovering / recovering_password are PINNED above — see
  // B-NO-STARTOVER-ON-RECOVERY.)
  return 'release';
}

// ---------------------------------------------------------------------------
// Storage clearing
// ---------------------------------------------------------------------------

const LOCAL_KEYS = ['takeover.sessionId', 'takeover.step', 'takeover.maxStep'];
const SESSION_KEYS_ALWAYS = ['takeover.lastCreateAt'];
const SESSION_TOKEN_KEY = 'sessionToken';

/**
 * Drop the persisted takeover session so a refresh / fresh POST starts clean.
 *
 * @param {{clearToken?: boolean}} [opts]
 *   clearToken=true (default, "hard" release): also remove the per-tab
 *     sessionToken — a fully fresh start (hydration release, "Start over").
 *   clearToken=false ("soft" lock release): keep the live token so the
 *     current tab's /state poll + screen keep working, while a refresh /
 *     new tab starts fresh.
 *
 * ALWAYS clears the idempotency key. This is CRITICAL: operator_review and
 * recovering are NOT in the backend terminal set, so a fresh POST /sessions
 * that reused the key would re-join the parked session (rejoined=true) and trap
 * the seller again. After clearing, the next POST mints a genuinely new session
 * (new buyer email + new DICloak profile — clean, no stale-state reuse).
 */
export function clearPersistedSession({ clearToken = true } = {}) {
  if (typeof window === 'undefined') return;
  try {
    LOCAL_KEYS.forEach((k) => window.localStorage.removeItem(k));
    SESSION_KEYS_ALWAYS.forEach((k) => window.sessionStorage.removeItem(k));
    if (clearToken) window.sessionStorage.removeItem(SESSION_TOKEN_KEY);
    clearIdempotencyKey();
  } catch {
    /* private-mode Safari / quota — non-fatal */
  }
}

export const __STORAGE = { LOCAL_KEYS, SESSION_KEYS_ALWAYS, SESSION_TOKEN_KEY };
